import { FormattedMessage, FormattedNumber } from 'react-intl';
import React, { Component } from 'react';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Pagination from '@liveauctioneers/caterwaul-components/lib/Pagination/Pagination';
import Review from './Review';
import Select from '@liveauctioneers/caterwaul-components/lib/Select/Select';
import styled from 'styled-components';
import styles from './Reviews.module.scss';

const VIEWS_TO_SHOW = [10, 25, 50, 100];

type Props = {
    dismissReviewsModal: Function;
    filter: string;
    filteredCount: number;
    fullPage: boolean;
    fullPageLink: string;
    helpfulCounts: any;
    mobile: boolean;
    onChange: Function;
    openReportReviewModal: Function;
    page: number;
    pageSize: number;
    reviewComments: any;
    reviews: any[];
    seller: any;
    setBidderReviewStatus: Function;
    sort: string;
    statuses: any;
    submitted: boolean;
    totalReviews: number;
    updateFilter: Function;
    updateSort: Function;
};

export default class Reviews extends Component<Props> {
    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    filterOptions = [
        { label: 'All Ratings', value: '' },
        { label: '5 Star Only', value: '5star' },
        { label: '4 Star Only', value: '4star' },
        { label: '3 Star Only', value: '3star' },
        { label: '2 Star Only', value: '2star' },
        { label: '1 Star Only', value: '1star' },
    ];

    sortOptions = [
        { label: 'Most Recent', value: '-created' },
        { label: 'Oldest', value: 'created' },
        { label: 'Highest Rated', value: '-overall' },
        { label: 'Lowest Rated', value: 'overall' },
    ];

    getPageEnds() {
        const { filteredCount, page, pageSize } = this.props;
        let start = (page - 1) * pageSize;
        let end = start + pageSize;

        if (end > filteredCount) {
            end = filteredCount;
        }

        if (start === 0 && end !== 0) {
            start = 1;
        }

        return { end, start };
    }

    closeModal = () => {
        this.props.dismissReviewsModal();
    };

    render() {
        const {
            filter,
            filteredCount,
            fullPage,
            fullPageLink,
            helpfulCounts,
            mobile,
            onChange,
            openReportReviewModal,
            page,
            pageSize,
            reviewComments,
            reviews,
            seller,
            setBidderReviewStatus,
            sort,
            statuses,
            submitted,
            totalReviews,
            updateFilter,
            updateSort,
        } = this.props;
        const commentsByReview = {};
        if (reviewComments) {
            Object.keys(reviewComments).forEach((k) => {
                commentsByReview[reviewComments[k].reviewId]
                    ? commentsByReview[reviewComments[k].reviewId].unshift(reviewComments[k])
                    : (commentsByReview[reviewComments[k].reviewId] = [reviewComments[k]]);
            });
        }
        const reviewDisplays = reviews
            ? reviews.map((item, i) => (
                  <Review
                      bidderCity={item.bidderCity}
                      bidderCountry={item.bidderCountry}
                      bidderFirstName={item.bidderFirstName}
                      bidderId={item.bidderId}
                      bidderState={item.bidderState}
                      catalogId={item.catalogId}
                      comments={commentsByReview[item.reviewId] || []}
                      helpfulCount={helpfulCounts[item.reviewId] || 0}
                      key={i}
                      mobile={mobile}
                      note={item.note}
                      openReportReviewModal={openReportReviewModal}
                      rating={item.overall}
                      review={item.review}
                      reviewHeadline={item.reviewHeadline}
                      reviewId={item.reviewId}
                      reviewSubmitted={item.reviewSubmitted}
                      seller={seller}
                      setBidderReviewStatus={setBidderReviewStatus}
                      statuses={statuses[item.reviewId] || []}
                      submitted={submitted}
                      wonItems={item.wonItems}
                  />
              ))
            : undefined;

        const latestTotalFormatted = <FormattedNumber value={reviews?.length || 0} />;
        const totalFormatted = <FormattedNumber value={filteredCount || 0} />;
        const { end, start } = this.getPageEnds();
        const startFormatted = <FormattedNumber value={start} />;
        const endFormatted = <FormattedNumber value={end} />;

        const title = fullPage ? (
            <FormattedMessage
                id="review.full_page_review_title"
                values={{ count: totalFormatted, end: endFormatted, start: startFormatted }}
            />
        ) : reviews?.length === 1 ? (
            <>
                <FormattedMessage id="review.latest_review" />
                <Link className={styles.seeAll} onClick={this.closeModal} route to={fullPageLink}>
                    <FormattedMessage id="review.see_all" />
                </Link>
            </>
        ) : (
            Boolean(reviews?.length) && (
                <>
                    <FormattedMessage id="review.latest_reviews" values={{ count: latestTotalFormatted }} />
                    <Link className={styles.seeAll} onClick={this.closeModal} route to={fullPageLink}>
                        <FormattedMessage id="review.see_all" />
                    </Link>
                </>
            )
        );

        const fullPageView = (
            <div className={styles.reviewsContainer}>
                <div className={styles.header} id="reviews">
                    <h2>{title}</h2>
                </div>
                <Pagination
                    filter={
                        <span className={styles.controls}>
                            <StyledSelect
                                hideSpacer
                                label={
                                    <StyledLabel>
                                        <FormattedMessage id="filter" />:
                                    </StyledLabel>
                                }
                                labelLeft
                                name="filter"
                                onChange={updateFilter}
                                options={this.filterOptions}
                                small
                                value={this.filterOptions.find((op) => op.value === filter)}
                            />
                            <StyledSelect
                                hideSpacer
                                label={
                                    <StyledLabel>
                                        <FormattedMessage id="sort" />:
                                    </StyledLabel>
                                }
                                labelLeft
                                name="sort"
                                onChange={updateSort}
                                options={this.sortOptions}
                                small
                                value={this.sortOptions.find((op) => op.value === sort)}
                            />
                        </span>
                    }
                    onChange={onChange}
                    pageSizeOptions={VIEWS_TO_SHOW}
                    paginationFilter={{
                        page: page,
                        pageSize: pageSize,
                        totalRecords: totalReviews,
                    }}
                >
                    {reviewDisplays}
                </Pagination>
            </div>
        );

        return fullPage ? (
            fullPageView
        ) : (
            <div className={styles.reviewsContainer}>
                <h2>{title}</h2>
                {reviewDisplays}
            </div>
        );
    }
}

const StyledSelect = styled(Select)`
    max-width: 260px;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        max-width: unset;
    }
`;

const StyledLabel = styled.span`
    && {
        letter-spacing: 1px;
        width: 60px;
        display: inline-block;
    }
`;
